import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";

type ViewMode = "reading" | "learning";

interface State {
	colors: boolean;
	viewMode: ViewMode;
	dialogAyahPreciseView: boolean;
	currentJuzForStages: number;
	lastPage: number | null;
}

interface Actions {
	toggleColors: () => void;
	updateViewMode: (newMode: ViewMode) => void;
	toggleDialogAyahPreciseView: () => void;
	setCurrentJuzForStages: (newJuz: number) => void;
	setLastPage: (page: number) => void;
}

const useSettingsStore = create<State & Actions>()(
	persist(
		(set, get) => ({
			colors: true,
			viewMode: "reading",
			dialogAyahPreciseView: false,
			currentJuzForStages: 1,
			lastPage: null,
			setLastPage: (page) => set({ lastPage: page }),
			setCurrentJuzForStages: (newJuz) => set({ currentJuzForStages: newJuz }),
			updateViewMode: (newMode: ViewMode) => set({ viewMode: newMode }),
			toggleColors: () => set({ colors: !get().colors }),
			toggleDialogAyahPreciseView: () =>
				set({ dialogAyahPreciseView: !get().dialogAyahPreciseView }),
		}),
		{
			name: "hifz-app-settings",
			partialize: (state) => ({
				colors: state.colors,
				viewMode: state.viewMode,
				dialogAyahPreciseView: state.dialogAyahPreciseView,
				currentJuzForStages: state.currentJuzForStages,
				lastPage: state.lastPage,
			}),
		},
	),
);

// Each hook selects an object; useShallow keeps it stable between unrelated
// store writes (the last page is saved on every page turn).
export const useColorsSettings = () =>
	useSettingsStore(useShallow(({ colors, toggleColors }) => ({ colors, toggleColors })));
export const useViewModeSettings = () =>
	useSettingsStore(useShallow(({ viewMode, updateViewMode }) => ({ viewMode, updateViewMode })));
export const useDialogAyahPreciseViewSettings = () =>
	useSettingsStore(
		useShallow(({ dialogAyahPreciseView, toggleDialogAyahPreciseView }) => ({
			dialogAyahPreciseView,
			toggleDialogAyahPreciseView,
		})),
	);
export const useCurrentJuzForStages = () =>
	useSettingsStore(
		useShallow(({ currentJuzForStages, setCurrentJuzForStages }) => ({
			currentJuzForStages,
			setCurrentJuzForStages,
		})),
	);
export const useLastPage = () =>
	useSettingsStore(useShallow(({ lastPage, setLastPage }) => ({ lastPage, setLastPage })));
